import React, { useEffect, useMemo, useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    CircularProgress,
    List,
    ListItemButton,
    ListItemText,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SensorsIcon from '@mui/icons-material/Sensors';
import { useSenseCapDevices, useSenseCapSamples } from '../../hooks/sensecap';
import CanvasGridDashboard from '../CanvasGridDashboard';
import HrvMetricsBar from '../sensecap/HrvMetricsBar';
import RhythmogramChart from '../sensecap/RhythmogramChart';
import RrHistogramChart from '../sensecap/RrHistogramChart';
import RrScatterogramChart from '../sensecap/RrScatterogramChart';
import { t } from '../../i18n/t';
import type {
    SenseCapSamplePoint,
    SenseCapSessionAnalysis,
} from '../../types/types';

type ChartPoint = {
    x: number;
    y: number;
    isRPeaks: boolean;
};

type SessionPage = {
    sessionIndex: number;
    startTime: string | null;
    endTime: string | null;
    uplinkCount: number;
    points: SenseCapSamplePoint[];
    amplitudePoints: ChartPoint[];
};

const formatDate = (value: string | null | undefined) => {
    if (!value) return '—';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
};

const buildSessionPages = (
    points: SenseCapSamplePoint[],
    analyses: SenseCapSessionAnalysis[],
): SessionPage[] => {
    const analysisBySession = new Map(analyses.map((a) => [a.sessionIndex, a]));
    const order: number[] = [];
    const groups = new Map<number, SenseCapSamplePoint[]>();

    for (const point of points) {
        if (!groups.has(point.sessionIndex)) {
            groups.set(point.sessionIndex, []);
            order.push(point.sessionIndex);
        }
        groups.get(point.sessionIndex)!.push(point);
    }

    return order.map((sessionIndex) => {
        const sessionPoints = groups.get(sessionIndex)!;
        const analysis = analysisBySession.get(sessionIndex);

        return {
            sessionIndex,
            startTime: analysis?.startTime ?? sessionPoints[0]?.time ?? null,
            endTime: analysis?.endTime ?? null,
            uplinkCount: analysis?.uplinkCount ?? 1,
            points: sessionPoints,
            amplitudePoints: sessionPoints.map((p) => ({
                x: p.tMs ?? p.x * 10,
                y: p.amplitude ?? p.y,
                isRPeaks: false,
            })),
        };
    });
};

const DashboardSenseCap = () => {
    const [selectedDevEui, setSelectedDevEui] = useState<string | undefined>();
    const [limit, setLimit] = useState('50');
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [sessionPage, setSessionPage] = useState(0);
    const [amplitudeOpen, setAmplitudeOpen] = useState(false);

    const { data: devices = [], isLoading: devicesLoading, error: devicesError } =
        useSenseCapDevices();

    const filters = useMemo(
        () => ({
            limit: limit ? Number(limit) : undefined,
            from: from || undefined,
            to: to || undefined,
        }),
        [limit, from, to],
    );

    const {
        data: samplesData,
        isLoading: samplesLoading,
        error: samplesError,
    } = useSenseCapSamples(selectedDevEui, filters);

    const sessionPages = useMemo(
        () =>
            samplesData?.points?.length
                ? buildSessionPages(samplesData.points, samplesData.analyses ?? [])
                : [],
        [samplesData],
    );

    const analysesBySession = useMemo(() => {
        const map = new Map<number, SenseCapSessionAnalysis>();
        for (const analysis of samplesData?.analyses ?? []) {
            map.set(analysis.sessionIndex, analysis);
        }
        return map;
    }, [samplesData]);

    useEffect(() => {
        setSessionPage(0);
    }, [selectedDevEui, samplesData]);

    const currentSession = sessionPages[sessionPage];
    const currentAnalysis = currentSession
        ? analysesBySession.get(currentSession.sessionIndex)
        : undefined;
    const amplitudePoints: ChartPoint[] = currentSession?.amplitudePoints?.length
        ? currentSession.amplitudePoints
        : [{ x: 0, y: 0, isRPeaks: false }];

    return (
        <Stack spacing={2} sx={{ width: '100%' }}>
            <Stack direction="row" alignItems="center" spacing={1}>
                <SensorsIcon color="primary" />
                <Typography variant="h5">{t('sensecap.title')}</Typography>
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="stretch">
                <Paper sx={{ width: { xs: '100%', md: 320 }, flexShrink: 0, p: 1 }}>
                    <Typography variant="subtitle2" sx={{ px: 1, py: 1 }}>
                        {t('sensecap.devices')}
                    </Typography>
                    {devicesLoading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                            <CircularProgress size={28} />
                        </Box>
                    )}
                    {devicesError && (
                        <Typography color="error" sx={{ px: 2, py: 1 }}>
                            {t('sensecap.loadDevicesError')}
                        </Typography>
                    )}
                    {!devicesLoading && !devices.length && (
                        <Typography color="text.secondary" sx={{ px: 2, py: 1 }}>
                            {t('sensecap.noDevices')}
                        </Typography>
                    )}
                    <List dense>
                        {devices.map((d) => (
                            <ListItemButton
                                key={d.devEui}
                                selected={selectedDevEui === d.devEui}
                                onClick={() => setSelectedDevEui(d.devEui)}
                            >
                                <ListItemText
                                    primary={d.devEui}
                                    secondary={t('sensecap.deviceMeta', {
                                        count: String(d.count),
                                        lastSeen: formatDate(d.lastSeen),
                                        rssi: d.rssi != null ? String(d.rssi) : '—',
                                    })}
                                />
                            </ListItemButton>
                        ))}
                    </List>
                </Paper>

                <Paper sx={{ flex: 1, p: 2, minWidth: 0 }}>
                    {!selectedDevEui ? (
                        <Typography color="text.secondary">{t('sensecap.selectDevice')}</Typography>
                    ) : (
                        <Stack spacing={2}>
                            <Typography variant="subtitle1">
                                {t('sensecap.chartFor', { devEui: selectedDevEui })}
                            </Typography>

                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                <TextField
                                    label={t('sensecap.limit')}
                                    type="number"
                                    size="small"
                                    value={limit}
                                    onChange={(e) => setLimit(e.target.value)}
                                    sx={{ width: 120 }}
                                />
                                <TextField
                                    label={t('sensecap.from')}
                                    type="datetime-local"
                                    size="small"
                                    value={from}
                                    onChange={(e) => setFrom(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ minWidth: 200 }}
                                />
                                <TextField
                                    label={t('sensecap.to')}
                                    type="datetime-local"
                                    size="small"
                                    value={to}
                                    onChange={(e) => setTo(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ minWidth: 200 }}
                                />
                            </Stack>

                            {samplesLoading && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                    <CircularProgress />
                                </Box>
                            )}
                            {samplesError && (
                                <Typography color="error">{t('sensecap.loadSamplesError')}</Typography>
                            )}
                            {samplesData && !samplesLoading && (
                                <Typography variant="body2" color="text.secondary">
                                    {t('sensecap.samplesMeta', {
                                        uplinks: String(samplesData.uplinkCount),
                                        sessions: String(samplesData.sessionCount ?? 0),
                                        points: String(samplesData.pointCount),
                                    })}
                                </Typography>
                            )}
                            {currentSession && !samplesLoading && (
                                <Typography variant="body2" color="text.secondary">
                                    {t('sensecap.sessionMeta', {
                                        uplinks: String(currentSession.uplinkCount),
                                        start: formatDate(currentSession.startTime),
                                        end: formatDate(currentSession.endTime),
                                        points: String(currentSession.points.length),
                                    })}
                                </Typography>
                            )}
                            {selectedDevEui && !samplesLoading && currentSession && (
                                <Stack spacing={2}>
                                    <HrvMetricsBar hrv={currentAnalysis?.hrv ?? null} />

                                    <Box>
                                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                            {t('sensecap.rhythmogram')}
                                        </Typography>
                                        <RhythmogramChart
                                            key={`rytmo-${currentSession.sessionIndex}`}
                                            tMs={currentAnalysis?.rhythmogram.tMs ?? []}
                                            rrMs={currentAnalysis?.rhythmogram.rrMs ?? []}
                                        />
                                    </Box>

                                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                                {t('sensecap.histogram')}
                                            </Typography>
                                            <RrHistogramChart
                                                key={`hist-${currentSession.sessionIndex}`}
                                                binEdges={currentAnalysis?.histogram.binEdges ?? []}
                                                counts={currentAnalysis?.histogram.counts ?? []}
                                            />
                                        </Box>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                                {t('sensecap.scatterogram')}
                                            </Typography>
                                            <RrScatterogramChart
                                                key={`scat-${currentSession.sessionIndex}`}
                                                x={currentAnalysis?.scatter.x ?? []}
                                                y={currentAnalysis?.scatter.y ?? []}
                                            />
                                        </Box>
                                    </Stack>

                                    <Accordion
                                        disableGutters
                                        expanded={amplitudeOpen}
                                        onChange={(_, open) => setAmplitudeOpen(open)}
                                    >
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <Typography variant="subtitle2">
                                                {t('sensecap.amplitude')}
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Box sx={{ width: '100%', overflow: 'hidden' }}>
                                                {amplitudeOpen && (
                                                <CanvasGridDashboard
                                                    key={`${selectedDevEui}-${currentSession.sessionIndex}`}
                                                    chartKey={`${selectedDevEui}-${currentSession.sessionIndex}`}
                                                    points={amplitudePoints}
                                                    singleChart
                                                    xMode="time"
                                                    hidePagination
                                                    xUnitsPerSecond={1000}
                                                />
                                                )}
                                            </Box>
                                        </AccordionDetails>
                                    </Accordion>

                                    {sessionPages.length > 1 && (
                                        <Stack
                                            direction="row"
                                            spacing={1}
                                            alignItems="center"
                                            justifyContent="center"
                                        >
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                disabled={sessionPage === 0}
                                                onClick={() => setSessionPage((p) => p - 1)}
                                            >
                                                {t('sensecap.prevSession')}
                                            </Button>
                                            <Typography variant="body2" color="text.secondary">
                                                {t('sensecap.sessionPage', {
                                                    current: String(sessionPage + 1),
                                                    total: String(sessionPages.length),
                                                })}
                                            </Typography>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                disabled={sessionPage >= sessionPages.length - 1}
                                                onClick={() => setSessionPage((p) => p + 1)}
                                            >
                                                {t('sensecap.nextSession')}
                                            </Button>
                                        </Stack>
                                    )}
                                </Stack>
                            )}
                        </Stack>
                    )}
                </Paper>
            </Stack>
        </Stack>
    );
};

export default DashboardSenseCap;
